<template>
    <div class="impression-stats">
        <div class="impression-stats__title-row">
            <div class="impression-stats__titlebar">
                <h2 class="impression-stats__title">Статистика показов</h2>
                <button class="update-btn" :class="{ 'update-btn--disabled': syncing, 'update-btn--spinning': syncing }"
                    :disabled="syncing" @click="updateStats" aria-label="Обновить статистику"></button>
            </div>
            <div class="impression-stats__actions" v-if="statsData && canExport">
                <button class="export-btn" :class="{ 'export-btn--disabled': exporting }" :disabled="exporting"
                    @click="exportToExcel" aria-label="Экспорт в Excel">
                    <span v-if="!exporting">Экспорт в Excel</span>
                    <span v-else>Загрузка...</span>
                </button>
            </div>
        </div>

        <!-- Фильтры -->
        <div class="impression-stats__filters">
            <div class="filters-row">
                <div class="filters-section filters-section--left">
                    <h3 class="filters-section__title">Выберите кампании:</h3>
                    <div class="filters-section__content">
                        <label class="checkbox-label">
                            <input type="checkbox" v-model="selectAllCampaigns" @change="toggleAllCampaigns" />
                            <span>Выбрать все</span>
                        </label>
                        <div class="checkbox-list">
                            <label v-for="campaign in campaigns" :key="campaign.id" class="checkbox-label">
                                <input type="checkbox" :value="campaign.id" v-model="selectedCampaignIds"
                                    @change="onCampaignsChange" />
                                <span>{{ campaign.name }}</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="filters-section filters-section--right" v-if="availableAdvertisements.length > 0">
                    <h3 class="filters-section__title">Выберите объявления:</h3>
                    <div class="filters-section__content">
                        <label class="checkbox-label">
                            <input type="checkbox" v-model="selectAllAdvertisements"
                                @change="toggleAllAdvertisements" />
                            <span>Выбрать все</span>
                        </label>
                        <div class="checkbox-list">
                            <label v-for="ad in availableAdvertisements" :key="ad.id" class="checkbox-label">
                                <input type="checkbox" :value="ad.id" v-model="selectedAdvertisementIds"
                                    @change="loadStats" />
                                <span>{{ ad.name || `Объявление #${ad.id}` }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Фильтр по датам -->
            <div class="filters-row filters-row--dates">
                <div class="filters-section filters-section--dates">
                    <h3 class="filters-section__title">Период:</h3>
                    <div class="filters-section__content filters-section__content--dates">
                        <div class="date-input-group">
                            <label class="date-label">
                                <span>От:</span>
                                <input type="date" v-model="startDate" @change="loadStats" class="date-input" />
                            </label>
                            <label class="date-label">
                                <span>До:</span>
                                <input type="date" v-model="endDate" @change="loadStats" class="date-input" />
                            </label>
                            <button @click="clearDateFilter" class="clear-dates-btn" v-if="startDate || endDate">
                                Сбросить
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Загрузка -->
        <div v-if="loading" class="impression-stats__loading">
            Загрузка данных...
        </div>

        <!-- Статистика -->
        <div v-else-if="statsData" class="impression-stats__content">
            <!-- Карточки с метриками -->
            <div class="stats-cards">
                <div class="stats-card">
                    <div class="stats-card__label">Всего показов</div>
                    <div class="stats-card__value">{{ statsData.stats.total_impressions }}</div>
                </div>
                <div class="stats-card">
                    <div class="stats-card__label">Уникальных инвентарей</div>
                    <div class="stats-card__value">{{ statsData.stats.unique_inventories }}</div>
                </div>
                <div class="stats-card">
                    <div class="stats-card__label">Средняя длительность</div>
                    <div class="stats-card__value">{{ formatDuration(statsData.stats.avg_duration) }}</div>
                </div>
                <div class="stats-card">
                    <div class="stats-card__label">Общая длительность</div>
                    <div class="stats-card__value">{{ formatDuration(statsData.stats.total_duration) }}</div>
                </div>
            </div>

            <!-- График показов по времени -->
            <div class="stats-chart">
                <h3 class="stats-chart__title">Показы по времени</h3>
                <div class="chart-container" v-if="hasChartData">
                    <canvas ref="timeChart"></canvas>
                </div>
                <div v-else class="chart-empty">
                    Нет данных для отображения графика
                </div>
            </div>

            <!-- Статистика по объявлениям -->
            <div class="stats-table">
                <h3 class="stats-table__title">Статистика по объявлениям</h3>
                <table class="table">
                    <thead>
                        <tr>
                            <th>Объявление</th>
                            <th>Количество показов</th>
                            <th>Средняя длительность</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="ad in statsData.by_advertisement" :key="ad.ad_id">
                            <td>{{ ad.ad_name || `Объявление #${ad.ad_id}` }}</td>
                            <td>{{ ad.count }}</td>
                            <td>{{ formatDuration(ad.avg_duration) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Статистика по рекламодателям -->
            <div class="stats-table" v-if="statsData.by_advertiser && statsData.by_advertiser.length > 0">
                <h3 class="stats-table__title">Статистика по рекламодателям</h3>
                <table class="table">
                    <thead>
                        <tr>
                            <th>Рекламодатель</th>
                            <th>Количество показов</th>
                            <th>Средняя длительность</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="advertiser in statsData.by_advertiser" :key="advertiser.advertiser_id || 'unknown'">
                            <td>{{ advertiser.advertiser_name || 'Без рекламодателя' }}</td>
                            <td>{{ advertiser.count }}</td>
                            <td>{{ formatDuration(advertiser.avg_duration) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Статистика по брендам -->
            <div class="stats-table" v-if="statsData.by_brand && statsData.by_brand.length > 0">
                <h3 class="stats-table__title">Статистика по брендам</h3>
                <table class="table">
                    <thead>
                        <tr>
                            <th>Бренд</th>
                            <th>Количество показов</th>
                            <th>Средняя длительность</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="brand in statsData.by_brand" :key="brand.brand_id || 'unknown'">
                            <td>{{ brand.brand_name || 'Без бренда' }}</td>
                            <td>{{ brand.count }}</td>
                            <td>{{ formatDuration(brand.avg_duration) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Статистика по экранам -->
            <div class="stats-table" v-if="statsData.by_screen && statsData.by_screen.length > 0">
                <h3 class="stats-table__title">Статистика по экранам</h3>
                <table class="table">
                    <thead>
                        <tr>
                            <th>Экран (GID)</th>
                            <th>Количество показов</th>
                            <th>Средняя длительность</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="screen in statsData.by_screen" :key="screen.inventory || 'unknown'">
                            <td>{{ screen.inventory || 'Не определен' }}</td>
                            <td>{{ screen.count }}</td>
                            <td>{{ formatDuration(screen.avg_duration) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Детальная таблица -->
            <div class="stats-details">
                <h3 class="stats-details__title">
                    Детальные данные
                    <span class="stats-details__count">({{ visibleImpressionsCount }} из {{ statsData.impressions.length }})</span>
                </h3>
                <div class="table-container">
                    <table class="table table--detailed">
                        <thead>
                            <tr>
                                <th>Время</th>
                                <th>Объявление ID</th>
                                <th>Инвентарь</th>
                                <th>Длительность</th>
                                <th>Координаты</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="impression in visibleImpressions" :key="impression.id">
                                <td>{{ formatTimestamp(impression.timestamp) }}</td>
                                <td>{{ impression.ad_id }}</td>
                                <td>{{ impression.inventory }}</td>
                                <td>{{ formatDuration(impression.duration) }}</td>
                                <td>
                                    <span
                                        v-if="impression.position && impression.position.lat && impression.position.lon">
                                        {{ parseFloat(impression.position.lat).toFixed(4) }}, {{
                                            parseFloat(impression.position.lon).toFixed(4) }}
                                    </span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="stats-details__load-more" v-if="hasMoreImpressions">
                    <button class="load-more-btn" @click="loadMoreImpressions">
                        Загрузить ещё 100
                    </button>
                </div>
            </div>
        </div>

        <!-- Пустое состояние -->
        <div v-else class="impression-stats__empty">
            Выберите кампании и объявления для просмотра статистики
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Chart from 'chart.js/auto';
import { useToast } from '../../composables/useToast';
import { notifyHttpError } from '../../utils/httpErrors';
import { formatTimestampMsInRtbTZ } from '../../utils/timezones';

export default {
    name: 'ImpressionStats',
    props: {
        campaigns: {
            type: Array,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            selectedCampaignIds: [],
            selectedAdvertisementIds: [],
            selectAllCampaigns: false,
            selectAllAdvertisements: false,
            loading: false,
            syncing: false,
            exporting: false,
            statsData: null,
            timeChart: null,
            startDate: '',
            endDate: '',
            impressionsLimit: 100,
        };
    },
    computed: {
        availableAdvertisements() {
            if (this.selectedCampaignIds.length === 0) {
                return [];
            }
            const ads = [];
            this.campaigns.forEach(campaign => {
                if (this.selectedCampaignIds.includes(campaign.id)) {
                    campaign.advertisements.forEach(ad => {
                        ads.push({
                            id: ad.id,
                            name: ad.name,
                            campaign_id: campaign.id,
                        });
                    });
                }
            });
            return ads;
        },
        hasChartData() {
            if (!this.statsData || !this.statsData.by_time) {
                return false;
            }
            const byTime = this.statsData.by_time;
            return Object.keys(byTime).length > 0;
        },
        canExport() {
            return this.selectedCampaignIds.length > 0 && this.selectedAdvertisementIds.length > 0;
        },
        visibleImpressions() {
            if (!this.statsData || !this.statsData.impressions) {
                return [];
            }
            return this.statsData.impressions.slice(0, this.impressionsLimit);
        },
        visibleImpressionsCount() {
            return this.visibleImpressions.length;
        },
        hasMoreImpressions() {
            if (!this.statsData || !this.statsData.impressions) {
                return false;
            }
            return this.statsData.impressions.length > this.impressionsLimit;
        },
    },
    methods: {
        toggleAllCampaigns() {
            if (this.selectAllCampaigns) {
                this.selectedCampaignIds = this.campaigns.map(c => c.id);
            } else {
                this.selectedCampaignIds = [];
            }
            this.onCampaignsChange();
        },
        toggleAllAdvertisements() {
            if (this.selectAllAdvertisements) {
                this.selectedAdvertisementIds = this.availableAdvertisements.map(ad => ad.id);
            } else {
                this.selectedAdvertisementIds = [];
            }
            this.loadStats();
        },
        onCampaignsChange() {
            // Сбрасываем выбор объявлений при изменении кампаний
            this.selectedAdvertisementIds = [];
            this.selectAllAdvertisements = false;
            this.loadStats();
        },
        async loadStats() {
            if (this.selectedCampaignIds.length === 0 || this.selectedAdvertisementIds.length === 0) {
                this.statsData = null;
                return;
            }

            this.loading = true;
            this.impressionsLimit = 100; // Сброс пагинации при новой загрузке
            try {
                const params = {
                    campaign_ids: this.selectedCampaignIds,
                    advertisement_ids: this.selectedAdvertisementIds,
                };

                // Добавляем фильтры по датам, если они выбраны
                if (this.startDate) {
                    params.start_date = this.startDate;
                }
                if (this.endDate) {
                    params.end_date = this.endDate;
                }

                const response = await axios.get('/api/impression-stats/data', {
                    params: params,
                });

                this.statsData = response.data;

                // Даем Vue время на обновление DOM
                await this.$nextTick();
                // Небольшая задержка для гарантии, что canvas готов
                setTimeout(() => {
                    this.renderChart();
                }, 100);
            } catch (error) {
                notifyHttpError(error, 'Не удалось загрузить статистику');
            } finally {
                this.loading = false;
            }
        },
        clearDateFilter() {
            this.startDate = '';
            this.endDate = '';
            this.loadStats();
        },
        loadMoreImpressions() {
            this.impressionsLimit += 100;
        },
        renderChart() {
            if (!this.statsData) {
                return;
            }

            if (!this.$refs.timeChart) {
                return;
            }

            // Уничтожаем предыдущий график, если есть
            if (this.timeChart) {
                this.timeChart.destroy();
                this.timeChart = null;
            }

            const byTime = this.statsData.by_time || {};
            const labels = Object.keys(byTime).sort();
            const data = labels.map(label => byTime[label]);

            // Если нет данных, показываем сообщение
            if (labels.length === 0) {
                return;
            }

            try {
                const ctx = this.$refs.timeChart.getContext('2d');

                // Форматируем метки для отображения
                const formattedLabels = labels.map(label => {
                    // Формат: "2025-11-11 09:00:00"
                    // Преобразуем в читаемый формат
                    const parts = label.split(' ');
                    const datePart = parts[0]; // "2025-11-11"
                    const timePart = parts[1]; // "09:00:00"
                    const [year, month, day] = datePart.split('-');
                    const [hour, minute] = timePart.split(':');

                    // Форматируем как "11.11 09:00"
                    return `${day}.${month} ${hour}:${minute}`;
                });

                this.timeChart = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels: formattedLabels,
                        datasets: [{
                            label: 'Показы',
                            data: data,
                            borderColor: 'rgb(75, 192, 192)',
                            backgroundColor: 'rgba(75, 192, 192, 0.2)',
                            tension: 0.1,
                            fill: true,
                        }],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                display: true,
                            },
                        },
                        scales: {
                            y: {
                                beginAtZero: true,
                                ticks: {
                                    stepSize: 1,
                                },
                            },
                            x: {
                                ticks: {
                                    maxRotation: 45,
                                    minRotation: 45,
                                },
                            },
                        },
                    },
                });
            } catch (error) {
                // Ошибка создания графика - игнорируем
            }
        },
        formatDuration(ms) {
            if (!ms) return '0 сек';
            const seconds = Math.floor(ms / 1000);
            if (seconds < 60) {
                return `${seconds} сек`;
            }
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes} мин ${remainingSeconds} сек`;
        },
        formatTimestamp(timestamp) {
            return formatTimestampMsInRtbTZ(timestamp);
        },
        async updateStats() {
            if (this.syncing) return;
            this.syncing = true;
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                const res = await fetch('/api/impression-stats/update', {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken || '',
                    },
                    credentials: 'same-origin',
                });
                if (!res.ok) throw new Error(await res.text());

                const response = await res.json();
                if (response.success) {
                    // Если есть выбранные кампании и объявления, перезагружаем статистику
                    if (this.selectedCampaignIds.length > 0 && this.selectedAdvertisementIds.length > 0) {
                        await this.loadStats();
                    }

                    // Показываем уведомление об успехе
                    const saved = response.saved || 0;
                    const message = saved > 0 
                        ? `Добавлено записей: ${saved}` 
                        : 'Новых данных нет';
                    useToast().success(message);
                } else {
                    useToast().error('Не удалось обновить статистику');
                }
            } catch (e) {
                useToast().error('Не удалось обновить статистику');
            } finally {
                this.syncing = false;
            }
        },
        async exportToExcel() {
            if (!this.canExport || this.exporting) {
                return;
            }

            this.exporting = true;
            try {
                // Создаем URL с параметрами
                const queryParams = new URLSearchParams();

                // Добавляем массивы с правильным форматом для Laravel
                this.selectedCampaignIds.forEach(id => {
                    queryParams.append('campaign_ids[]', id);
                });
                this.selectedAdvertisementIds.forEach(id => {
                    queryParams.append('advertisement_ids[]', id);
                });

                if (this.startDate) {
                    queryParams.append('start_date', this.startDate);
                }
                if (this.endDate) {
                    queryParams.append('end_date', this.endDate);
                }

                const url = `/api/impression-stats/export?${queryParams.toString()}`;

                // Получаем CSRF токен
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

                // Выполняем запрос для скачивания файла
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    throw new Error('Ошибка при экспорте данных');
                }

                // Получаем имя файла из заголовка или используем дефолтное
                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = 'impression-stats.xlsx';
                if (contentDisposition) {
                    // Пробуем извлечь имя файла из разных форматов
                    let filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
                    if (filenameMatch) {
                        filename = decodeURIComponent(filenameMatch[1]);
                    } else {
                        filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
                        if (filenameMatch) {
                            filename = filenameMatch[1];
                        }
                    }
                }

                // Убеждаемся, что расширение .xlsx
                if (!filename.endsWith('.xlsx')) {
                    filename = filename.replace(/\.xlsx_?$/, '') + '.xlsx';
                }

                // Создаем blob и скачиваем файл
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            } catch (error) {
                useToast().error('Не удалось выгрузить данные в Excel');
            } finally {
                this.exporting = false;
            }
        },
    },
    watch: {
        availableAdvertisements() {
            // Обновляем выбор "все" при изменении доступных объявлений
            if (this.selectAllAdvertisements && this.availableAdvertisements.length > 0) {
                this.selectedAdvertisementIds = this.availableAdvertisements.map(ad => ad.id);
            }
        },
    },
    beforeUnmount() {
        if (this.timeChart) {
            this.timeChart.destroy();
        }
    },
};
</script>

<style lang="scss" scoped>
.impression-stats {
    padding: var(--gap, 16px);
    max-width: 1400px;
    margin: 0 auto;

    &__title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--gap, 16px);
        height: 37.33px;
    }

    &__titlebar {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-left: auto;
    }

    &__title {
        margin: 0;
        font-size: var(--font-size-2xl, 20px);
        font-weight: 600;
        color: var(--accent, #0f172a);
    }

    &__filters {
        background: #f8fafc;
        border-radius: 8px;
        padding: 1.25rem;
        margin-bottom: 2rem;
        border: 1px solid #e2e8f0;
    }

    &__loading {
        text-align: center;
        padding: 3rem;
        color: #666;
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }

    &__empty {
        text-align: center;
        padding: 3rem;
        color: #999;
        font-size: 1.1rem;
    }
}

.filters-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1.5rem;
    }
}

.filters-section {
    &__title {
        font-size: 0.95rem;
        font-weight: 600;
        margin-bottom: 0.75rem;
        color: #334155;
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    padding: 0.25rem 0;
    user-select: none;

    &:hover {
        opacity: 0.8;
    }

    input[type="checkbox"] {
        cursor: pointer;
        width: 16px;
        height: 16px;
        accent-color: #3b82f6;
    }

    span {
        user-select: none;
        color: #475569;
        font-size: 0.9rem;
    }
}

.checkbox-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-left: 1.25rem;
    max-height: 200px;
    overflow-y: auto;
    padding-right: 0.25rem;

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f5f9;
    }

    &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 2px;
    }
}

.stats-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
}

.stats-card {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 1.5rem;
    text-align: center;

    &__label {
        font-size: 0.9rem;
        color: #666;
        margin-bottom: 0.5rem;
    }

    &__value {
        font-size: 2rem;
        font-weight: 600;
        color: #333;
    }
}

.stats-chart {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 1.5rem;

    &__title {
        font-size: 1.2rem;
        font-weight: 600;
        margin-bottom: 1rem;
        color: #333;
    }
}

.chart-container {
    position: relative;
    height: 300px;
}

.chart-empty {
    padding: 3rem;
    text-align: center;
    color: #999;
    font-style: italic;
}

.stats-table,
.stats-details {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 1.5rem;

    &__title {
        font-size: 1.2rem;
        font-weight: 600;
        margin-bottom: 1rem;
        color: #333;
    }
}

.stats-details {
    &__count {
        font-size: 0.9rem;
        font-weight: 400;
        color: #666;
        margin-left: 0.5rem;
    }

    &__load-more {
        margin-top: 1rem;
        text-align: center;
    }
}

.load-more-btn {
    padding: 0.6rem 1.5rem;
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background: #e2e8f0;
        border-color: #94a3b8;
        color: #334155;
    }
}

.table {
    width: 100%;
    border-collapse: collapse;

    thead {
        background: #f5f5f5;
    }

    th,
    td {
        padding: 0.75rem;
        text-align: left;
        border-bottom: 1px solid #e0e0e0;
    }

    th {
        font-weight: 600;
        color: #333;
    }

    tbody tr:hover {
        background: #f9f9f9;
    }

    &--detailed {
        font-size: 0.9rem;
    }
}

.table-container {
    max-height: 400px;
    overflow-y: auto;
}

.filters-row--dates {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid #e2e8f0;
}

.filters-section--dates {
    width: 100%;
}

.filters-section__content--dates {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
}

.date-input-group {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
}

.date-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #475569;

    span {
        min-width: 30px;
        color: #64748b;
    }
}

.date-input {
    padding: 0.5rem 0.75rem;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 0.9rem;
    cursor: pointer;
    background: #ffffff;
    color: #334155;
    transition: border-color 0.2s ease;
    min-width: 150px;

    &:hover {
        border-color: #94a3b8;
    }

    &:focus {
        outline: none;
        border-color: #3b82f6;
    }
}

.clear-dates-btn {
    padding: 0.5rem 1rem;
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    color: #475569;
    transition: all 0.2s ease;

    &:hover {
        background: #e2e8f0;
        border-color: #94a3b8;
        color: #334155;
    }
}

.export-btn {
    padding: 0.5rem 1rem;
    background: #10b981;
    color: #ffffff;
    border: 1px solid #059669;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;

    &:hover:not(:disabled) {
        background: #059669;
        border-color: #047857;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
    }

    &:disabled,
    &--disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
}
</style>
